import { useEffect, useState, useRef } from 'react';
import useAuthStore from '../store/useAuthStore';
import { reportBootEvent } from '../utils/bootDiagnostics';

const STUCK_LOADING_MS = 10_000;
const STUCK_BLANK_MS = 12_000;

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 100000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const cardStyle = {
  maxWidth: 360,
  width: '100%',
  background: '#fff',
  borderRadius: 20,
  padding: '28px 24px',
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
};

const titleStyle = { margin: '0 0 8px', fontSize: 18, fontWeight: 800, color: '#1e293b' };
const textStyle = { margin: '0 0 20px', fontSize: 13, lineHeight: 1.5, color: '#64748b' };

const btnPrimary = {
  width: '100%',
  padding: '14px 20px',
  border: 'none',
  borderRadius: 14,
  background: '#2563eb',
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  marginBottom: 10,
};

const btnSecondary = {
  width: '100%',
  padding: '12px 20px',
  border: 'none',
  borderRadius: 14,
  background: '#f1f5f9',
  color: '#475569',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

/** Показывается, если React смонтировался, но интерфейс «завис»:
 *  - loading=true дольше STUCK_LOADING_MS (зависшая верификация)
 *  - ИЛИ loading=false, isAuthenticated=false, но мы не на /login
 *    (зависший Navigate → белый экран)
 */
export default function BootstrapRecovery() {
  const loading = useAuthStore((s) => s.loading);
  const [stuck, setStuck] = useState(false);
  const [reason, setReason] = useState('loading');
  const blankTimerRef = useRef(null);

  // Вотчдог на stuck loading
  useEffect(() => {
    if (!loading) {
      setStuck(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setStuck(true);
      setReason('loading');
      reportBootEvent('bootstrap_recovery_shown', { afterMs: STUCK_LOADING_MS, reason: 'loading' });
    }, STUCK_LOADING_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  // Вотчдог на «белый экран»: loading=false, isAuthenticated=false, путь не /login
  useEffect(() => {
    if (loading) {
      if (blankTimerRef.current) {
        clearTimeout(blankTimerRef.current);
        blankTimerRef.current = null;
      }
      return undefined;
    }

    const checkBlank = () => {
      const { isAuthenticated } = useAuthStore.getState();
      if (!isAuthenticated && window.location.pathname !== '/login') {
        reportBootEvent('bootstrap_recovery_shown', {
          afterMs: STUCK_BLANK_MS,
          reason: 'blank',
          path: window.location.pathname,
        });
        setStuck(true);
        setReason('blank');
      }
    };

    // Проверяем не сразу, а через небольшой таймаут (даём время Navigate сработать)
    blankTimerRef.current = setTimeout(checkBlank, STUCK_BLANK_MS);
    return () => {
      if (blankTimerRef.current) {
        clearTimeout(blankTimerRef.current);
        blankTimerRef.current = null;
      }
    };
  }, [loading]);

  if (!stuck) return null;

  return (
    <div style={overlayStyle} role="alertdialog" aria-labelledby="bootstrap-recovery-title">
      <div style={cardStyle}>
        <p id="bootstrap-recovery-title" style={titleStyle}>
          {reason === 'loading'
            ? 'Приложение долго загружается'
            : 'Страница не загрузилась'}
        </p>
        <p style={textStyle}>
          {reason === 'loading'
            ? 'Обычно помогает перезагрузка страницы. Если интернет нестабилен — попробуйте продолжить с сохранённой сессией.'
            : 'Похоже, страница отобразилась некорректно. Попробуйте перезагрузить.'}
        </p>
        <button type="button" style={btnPrimary} onClick={() => window.location.reload()}>
          Перезагрузить
        </button>
        <button
          type="button"
          style={btnSecondary}
          onClick={() => {
            useAuthStore.getState().forceBootstrapExit();
            setStuck(false);
          }}
        >
          Продолжить
        </button>
      </div>
    </div>
  );
}
